import { useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';

import { IconButton } from '@atlaskit/button-next/new';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';

import { Image } from '@/libraries/design/components/image/src/image';

// Substitutions for Loom-internal design-token CSS variables from @loomhq/lens
const LNS_RADIUS_XLARGE = '16px';
const LNS_RADIUS_FULL = '9999px';
const LNS_SHADOW_LARGE = '0 8px 24px rgba(0,0,0,0.15)';
const LNS_COLOR_WHITE = '#ffffff';

const DEFAULT_VIDEO_SRC =
	'https://dam-cdn.atl.orangelogic.com/AssetLink/0d02836qy6s1j2k4ew7my0rcg4w063q0.mp4';
// Fallback persona background used when neither `imageSrc` nor `fullImgSrcPath`
// is provided. Replaces the Loom-internal `@assets/img/onboarding/Other.jpg`
// webpack alias from the original component.
const DEFAULT_PERSONA_BACKGROUND =
	'https://dam-cdn.atl.orangelogic.com/AssetLink/hq00j44fl1jlm2vr2pev4lsf2o4cf36n.jpg';

// --- Layout substitutes for `<Arrange gap="medium" autoFlow="row">` and
// `<Align alignment="topCenter">` from @loomhq/lens ------------------------
const arrangeCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
});

const alignTopCenterCss = css({
	display: 'flex',
	justifyContent: 'center',
});

// --- Media wrapper + image ------------------------------------------------
const mediaWrapCss = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// Clip all overlays (gradient, timeline, emoji spans) to the same
	// rounded rectangle as the background image so the corners line up
	// pixel-perfectly.
	borderRadius: LNS_RADIUS_XLARGE,
	overflow: 'hidden',
});

const imageCss = css({
	height: 'auto',
	width: '100%',
	display: 'block',
});

const timelineGradientCss = css({
	position: 'absolute',
	insetBlockEnd: 0,
	backgroundImage: 'linear-gradient(to top, hsla(0, 0%, 0%, 0.6), transparent)',
	height: '10%',
	width: '100%',
});

const videoWrapCss = css({
	position: 'absolute',
	insetInlineEnd: '3%',
	insetBlockStart: '6%',
	width: '22.3%',
	aspectRatio: '1 / 1',
	borderRadius: LNS_RADIUS_FULL,
	overflow: 'hidden',
	boxShadow: LNS_SHADOW_LARGE,
});

const videoCss = css({
	height: '100%',
	width: '100%',
	objectFit: 'cover',
	objectPosition: 'center',
});

// --- Timeline bar ---------------------------------------------------------
const timelineCss = css({
	backgroundColor: 'hsla(0, 0%, 100%, 0.3)',
	borderRadius: LNS_RADIUS_FULL,
	insetBlockEnd: '4%',
	height: '1.1%',
	insetInlineStart: '4.5%',
	position: 'absolute',
	width: '91%',
});

const timelineChildCss = css({
	position: 'absolute',
});

const timelineFillWrapCss = css({
	insetInlineStart: 0,
	insetBlockStart: 0,
	display: 'block',
	height: '100%',
	overflow: 'hidden',
	borderRadius: LNS_RADIUS_FULL,
	transform: 'translateX(0)',
	width: '100%',
});

// Raw CSS animation injected via a <style> tag — bypasses Compiled's
// keyframes handling, which was silently failing to link the keyframes to
// the animation-name
const FILL_ANIMATION_STYLE = `
@keyframes wac-loom-timeline-fill {
	0%, 10% { width: 0%; }
	100% { width: 100%; }
}
.wac-loom-timeline-fill {
	animation: wac-loom-timeline-fill 10s linear infinite;
}
.wac-loom-timeline-fill--paused {
	animation-play-state: paused;
}
@media (prefers-reduced-motion: reduce) {
	.wac-loom-timeline-fill {
		animation: none;
		width: 79%;
	}
}
`;

const timelineFillCss = css({
	position: 'absolute',
	insetInlineStart: 0,
	insetBlockStart: 0,
	backgroundColor: LNS_COLOR_WHITE,
	height: '100%',
	width: '0%',
});

// --- Emoji markers --------------------------------------------------------
const timelineEmojiCss = css({
	position: 'absolute',
	insetBlockEnd: '150%',
	// Width matches the SVG's width so the emoji sits exactly at its
	// `left: X%` offset.
	width: '7%',
	opacity: 0,
	transform: 'translateY(10px)',
});

const emojiSvgCss = css({
	width: '100%',
	display: 'block',
});

// Emoji animations use raw CSS keyframes (via a <style> tag)
const EMOJI_ANIMATION_STYLE = `
@keyframes wac-loom-emoji-1 {
	0%, 8%, 32% { opacity: 0; transform: translateY(10px); }
	10%, 30% { opacity: 1; transform: translateY(0); }
}
@keyframes wac-loom-emoji-2 {
	0%, 13%, 37% { opacity: 0; transform: translateY(10px); }
	15%, 35% { opacity: 1; transform: translateY(0); }
}
@keyframes wac-loom-emoji-3 {
	0%, 26%, 50% { opacity: 0; transform: translateY(10px); }
	28%, 48% { opacity: 1; transform: translateY(0); }
}
@keyframes wac-loom-emoji-4 {
	0%, 34%, 58% { opacity: 0; transform: translateY(10px); }
	36%, 56% { opacity: 1; transform: translateY(0); }
}
@keyframes wac-loom-emoji-5 {
	0%, 42%, 66% { opacity: 0; transform: translateY(10px); }
	44%, 64% { opacity: 1; transform: translateY(0); }
}
@keyframes wac-loom-emoji-6 {
	0%, 58%, 82% { opacity: 0; transform: translateY(10px); }
	60%, 80% { opacity: 1; transform: translateY(0); }
}
@keyframes wac-loom-emoji-7 {
	0%, 70%, 94% { opacity: 0; transform: translateY(10px); }
	72%, 92% { opacity: 1; transform: translateY(0); }
}
@keyframes wac-loom-emoji-8 {
	0%, 74%, 98% { opacity: 0; transform: translateY(10px); }
	76%, 96% { opacity: 1; transform: translateY(0); }
}
@keyframes wac-loom-emoji-9 {
	0%, 76%, 100% { opacity: 0; transform: translateY(10px); }
	78%, 98% { opacity: 1; transform: translateY(0); }
}
.wac-loom-emoji-1 { animation: wac-loom-emoji-1 10s linear infinite; }
.wac-loom-emoji-2 { animation: wac-loom-emoji-2 10s linear infinite; }
.wac-loom-emoji-3 { animation: wac-loom-emoji-3 10s linear infinite; }
.wac-loom-emoji-4 { animation: wac-loom-emoji-4 10s linear infinite; }
.wac-loom-emoji-5 { animation: wac-loom-emoji-5 10s linear infinite; }
.wac-loom-emoji-6 { animation: wac-loom-emoji-6 10s linear infinite; }
.wac-loom-emoji-7 { animation: wac-loom-emoji-7 10s linear infinite; }
.wac-loom-emoji-8 { animation: wac-loom-emoji-8 10s linear infinite; }
.wac-loom-emoji-9 { animation: wac-loom-emoji-9 10s linear infinite; }
.wac-loom-emoji--paused { animation-play-state: paused; }
@media (prefers-reduced-motion: reduce) {
	.wac-loom-emoji-1, .wac-loom-emoji-2, .wac-loom-emoji-3,
	.wac-loom-emoji-4, .wac-loom-emoji-5, .wac-loom-emoji-6,
	.wac-loom-emoji-7, .wac-loom-emoji-8, .wac-loom-emoji-9 {
		animation: none;
		opacity: 0;
	}
}
`;

// Module-level flag + attribute marker so the raw CSS animation rules are
// injected into `document.head` exactly once per document, no matter how
// many `<LoomAnimation>` instances mount. Guarded for SSR (no `document`).
const LOOM_ANIMATION_STYLE_ATTR = 'data-wac-loom-animation-styles';
let loomAnimationStylesInjected = false;

const injectLoomAnimationStyles = (): void => {
	if (loomAnimationStylesInjected || typeof document === 'undefined') {
		return;
	}
	// Bail if another instance (or an HMR reload) already added the tag.
	if (document.querySelector(`style[${LOOM_ANIMATION_STYLE_ATTR}]`)) {
		loomAnimationStylesInjected = true;
		return;
	}
	const style = document.createElement('style');
	style.setAttribute(LOOM_ANIMATION_STYLE_ATTR, '');
	style.textContent = `${FILL_ANIMATION_STYLE}${EMOJI_ANIMATION_STYLE}`;
	document.head.appendChild(style);
	loomAnimationStylesInjected = true;
};

const EMOJI_MARKERS: ReadonlyArray<{ n: number; left: string; icon: string }> = [
	{ n: 1, left: '1%', icon: '😍' },
	{ n: 2, left: '6%', icon: '😍' },
	{ n: 3, left: '19%', icon: '🙌' },
	{ n: 4, left: '35%', icon: '👍' },
	{ n: 5, left: '43%', icon: '👍' },
	{ n: 6, left: '69%', icon: '😮' },
	{ n: 7, left: '85%', icon: '😍' },
	{ n: 8, left: '90%', icon: '🙌' },
	{ n: 9, left: '95%', icon: '👍' },
];

const EmojiTimeline = ({ isPaused }: { isPaused: boolean }): JSX.Element => (
	<>
		{EMOJI_MARKERS.map(({ n, left, icon }) => (
			<span
				key={n}
				className={isPaused ? `wac-loom-emoji-${n} wac-loom-emoji--paused` : `wac-loom-emoji-${n}`}
				css={[timelineChildCss, timelineEmojiCss]}
				style={{ left }}
			>
				<svg css={emojiSvgCss} viewBox="0 0 30 30">
					<text x="3" y="23">
						{icon}
					</text>
				</svg>
			</span>
		))}
	</>
);

/**
 * Loom-style animated persona card: a persona still image with an animated
 * webcam bubble video, gradient overlay, animated timeline fill, and emoji
 * reactions that appear along the timeline.
 *
 * Ported from Loom's internal `LoomAnimation` component.
 */
export const LoomAnimation = (): JSX.Element => {
	const [isPlaying, setIsPlaying] = useState(true);
	// Bumped every time playback (re)starts. Used as a `key` on the timeline
	// wrapper so the CSS animations remount and replay from 0% instead of
	// resuming from wherever they were paused.
	const [runId, setRunId] = useState(0);
	const videoRef = useRef<HTMLVideoElement>(null);

	// Insert the shared keyframe/class rules into `document.head` once for
	// the lifetime of the page, regardless of how many `<LoomAnimation>`
	// instances mount.
	useEffect(() => {
		injectLoomAnimationStyles();
	}, []);

	const handlePlayPause = () => {
		if (isPlaying) {
			videoRef.current?.pause();
			setIsPlaying(false);
		} else {
			// Restart the video from the beginning so the user sees the whole
			// clip alongside the full left-to-right timeline animation.
			if (videoRef.current) {
				videoRef.current.currentTime = 0;
			}
			// `HTMLMediaElement.play()` returns a Promise that rejects when the
			// browser blocks playback (e.g. autoplay policy). If it rejects we
			// have to flip our optimistic `isPlaying=true` back to `false` so
			// the UI state matches the actual video state.
			const playPromise = videoRef.current?.play();
			if (playPromise && typeof playPromise.catch === 'function') {
				playPromise.catch(() => setIsPlaying(false));
			}
			setRunId((n) => n + 1);
			setIsPlaying(true);
		}
	};

	const personaBackgroundImage = DEFAULT_PERSONA_BACKGROUND;
	const videoSrc = DEFAULT_VIDEO_SRC;

	return (
		<div css={arrangeCss}>
			<div css={mediaWrapCss}>
				{personaBackgroundImage ? (
					<Image css={imageCss} alt="" src={personaBackgroundImage} height={394} width={700} />
				) : null}
				<div css={timelineGradientCss} />
				<div css={videoWrapCss}>
					<video
						ref={videoRef}
						autoPlay
						css={videoCss}
						height="648"
						loop
						muted
						playsInline
						src={videoSrc}
						width="648"
					/>
				</div>
				<div css={timelineCss} key={runId}>
					<span css={[timelineChildCss, timelineFillWrapCss]}>
						<span
							className={
								isPlaying
									? 'wac-loom-timeline-fill'
									: 'wac-loom-timeline-fill wac-loom-timeline-fill--paused'
							}
							css={timelineFillCss}
						/>
					</span>
					<EmojiTimeline isPaused={!isPlaying} />
				</div>
			</div>
			<div css={alignTopCenterCss}>
				{isPlaying ? (
					<IconButton
						appearance="subtle"
						label="Pause animation"
						icon={VideoPauseOverlayIcon}
						onClick={handlePlayPause}
						isTooltipDisabled={true}
					/>
				) : (
					<IconButton
						appearance="subtle"
						label="Play animation"
						icon={VideoPlayOverlayIcon}
						onClick={handlePlayPause}
						isTooltipDisabled={true}
					/>
				)}
			</div>
		</div>
	);
};
